import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../utils/api';
import { formatKrw } from '../../utils/currency';
import { REFUND_PROCESSING_DAYS } from '../../constants/legal';
import { VPCard } from './primitives';

/**
 * Settings › Payments — the customer's own money history.
 *
 * WHY THIS EXISTS: GET /profile/membership has always returned a `payments`
 * array (api/profile.py) and SettingsPage fetched it and threw it away.
 * GET /refund-requests shipped with zero frontend callers. So a customer could
 * file a refund request and then find no surface anywhere that could tell them
 * a single thing had happened — which is the complaint this whole change
 * started from. Both halves here are reads of endpoints that already existed.
 *
 * Everything is self-scoped server-side (both queries filter on the caller's
 * own user_id). This component sends no ids and can request no one else's data.
 */

/* Chip palette, matching the team-status chip already in SettingsPage. */
const OK = { bg: '#DCFCE7', fg: '#15803D' };
const WARN = { bg: '#FEF3C7', fg: '#92400E' };
const BAD = { bg: '#FEE2E2', fg: '#991B1B' };
const MUTED = { bg: 'var(--border-color)', fg: 'var(--text-secondary)' };

/**
 * The COMPLETE payment vocabulary — SEVEN values, pinned by the CHECK constraint
 * in backend/db/migrations/045_wire_payment_lifecycle.sql.
 *
 * ⚠ NOT the list in payment_models.py's comment. That comment is stale: it
 * invents 'failed' (forbidden by the 045 CHECK, and no code writes it) and omits
 * 'paid', 'rejected' and 'expired' — the three that matter most on this screen.
 *
 * ⚠ 'paid' and 'completed' MUST share one label. The card path writes
 * 'completed'; the reconcile sweep rewrites it to 'paid' (`ELSE 'paid'`). Same
 * money, two words, depending only on whether a sweep has touched the row.
 */
const PAYMENT_STATUS = {
  completed:          { ko: '결제 완료',   en: 'Paid',                 settled: true,  ...OK },
  paid:               { ko: '결제 완료',   en: 'Paid',                 settled: true,  ...OK },
  partially_refunded: { ko: '부분 환불',   en: 'Partially refunded',   settled: true,  ...WARN },
  refunded:           { ko: '환불 완료',   en: 'Refunded',             settled: true,  ...MUTED },
  pending:            { ko: '입금 대기',   en: 'Awaiting deposit',     settled: false, ...WARN },
  rejected:           { ko: '입금 미확인', en: 'Deposit not received', settled: false, ...BAD },
  expired:            { ko: '기한 만료',   en: 'Request expired',      settled: false, ...MUTED },
};

/* The COMPLETE refund-request vocabulary. `approved` and `closed` are overridden
   at render time by money_released — see requestLabel(). */
const REQUEST_STATUS = {
  open:      { ko: '접수됨',  en: 'Received',  ...WARN },
  in_review: { ko: '검토 중', en: 'In review', ...WARN },
  approved:  { ko: '승인됨',  en: 'Approved',  ...OK },
  denied:    { ko: '거절됨',  en: 'Declined',  ...BAD },
  closed:    { ko: '종료됨',  en: 'Closed',    ...MUTED },
};

/* wire_transfer and bank_transfer are the same thing written by two writers. */
const METHOD = {
  wire_transfer: { ko: '계좌이체', en: 'Bank transfer' },
  bank_transfer: { ko: '계좌이체', en: 'Bank transfer' },
  card:          { ko: '카드',     en: 'Card' },
  cash:          { ko: '현금',     en: 'Cash' },
  other:         { ko: '기타',     en: 'Other' },
};

/**
 * A-02 (.claude/TODO/Portone-KGinisys-Pricing-fix-10-2.md §4.3, T2.10). Until
 * 2026-10-03 the card path stored "Card payment via PortOne (NHN KCP)": the
 * name of another PG, and on a PortOne 계좌이체 too. New rows store
 * PORTONE_PLAN_LABEL (payment_completion_service.py, pinned by
 * backend/tests/test_portone_payments.py); rows already stored keep the old
 * text, so it is relabelled here, on read, and nothing is rewritten.
 *
 * One label in both locales: descriptions are stored text, printed verbatim
 * in either language, and this is exactly what a new row already prints.
 *
 * ⚠ `provider` is not in this payload today: GET /profile/membership
 * (api/profile.py) selects no provider column. When a row does carry one, it is
 * relabelled only if that provider is 'portone'; when it does not, the
 * description alone identifies it, since only the PortOne completion path ever
 * wrote that text, always with provider 'portone'.
 */
const LEGACY_PORTONE_DESCRIPTION = 'Card payment via PortOne';
const PORTONE_PLAN_LABEL = '연간플랜 결제 (PortOne · KG이니시스)';

const describePayment = (p, isKo) => {
  const d = p.description;
  if (typeof d === 'string' && d.startsWith(LEGACY_PORTONE_DESCRIPTION)
      && (p.provider == null || p.provider === 'portone')) {
    return PORTONE_PLAN_LABEL;
  }
  return d || (isKo ? '결제' : 'Payment');
};

/* payment_date is a DATE serialised as 'YYYY-MM-DD'. new Date() would read that
   as UTC midnight and render a day early for any viewer west of UTC, so slice
   instead — the same choice the admin surface made. Also correct for the full
   ISO timestamps (submitted_at / resolved_at / released_at). */
const ymd = (v) => (v ? String(v).slice(0, 10) : '—');

/* An UNRECOGNISED status is not assumed unsettled. The 045 CHECK is NOT VALID,
   so a historical row may legitimately carry a value outside the seven — and
   those old rows are exactly the ones a customer is most likely to be querying.
   Unknown goes in the main ledger with a neutral chip. Never guess a side. */
const isUnsettled = (s) => PAYMENT_STATUS[s]?.settled === false;

function Chip({ view, isKo, fallback }) {
  const v = view || { ...MUTED, ko: fallback || '확인 중', en: fallback || 'Processing' };
  return (
    <span style={{
      fontSize: 12, padding: '2px 8px', borderRadius: 4,
      background: v.bg, color: v.fg, whiteSpace: 'nowrap',
    }}>
      {isKo ? v.ko : v.en}
    </span>
  );
}

/**
 * The misleading-state rules live here, not in the table markup.
 *
 * 'closed' is written by TWO different things: the real release (which also
 * writes released_refund_id) and an admin status change (which never does, and
 * is permitted from 'denied'). So a declined-and-filed request and a
 * paid-out request are the same word. Rendering a bare "Closed" shows identical
 * pixels for "we paid you" and "we said no".
 *
 * 'approved' is not a promise of payment either: the permission that sets it is
 * self-grantable, so it is not evidence a trusted human reviewed anything. Only
 * money_released means money moved — which is why no 'approved' label here
 * carries a won figure.
 */
function requestLabel(r, isKo) {
  const released = !!r.money_released;

  if (r.status === 'closed') {
    return released
      ? { ...OK, ko: '환불 완료', en: 'Refunded' }
      : { ...MUTED, ko: '종료 — 환불 없음', en: 'Closed — no refund issued' };
  }

  if (r.status === 'approved' && !released) {
    // A refund whose PortOne call failed in transit leaves the request
    // 'approved' forever: the row that would close it is never written. We
    // cannot see the stranded 'processing' row from here, so age is the only
    // signal. Anything candid enough to describe the real uncertainty invites a
    // chargeback; anything reassuring enough to avoid one is a lie. Route it to
    // a human instead — this is the only state that gets a support line.
    const resolved = r.resolved_at ? Date.parse(r.resolved_at) : NaN;
    const stale = Number.isFinite(resolved) && (Date.now() - resolved) > 24 * 3600 * 1000;
    if (stale) {
      return { ...WARN, ko: '승인됨 — 지급 처리 중', en: 'Approved — payout in progress', support: true };
    }
  }

  return REQUEST_STATUS[r.status] || null;
}

function PaymentRow({ p, isKo, requested }) {
  const back = Number(p.refunded_krw || 0);
  const method = METHOD[p.payment_method];
  return (
    <tr style={{ borderTop: '1px solid var(--border-color)' }}>
      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{ymd(p.payment_date)}</td>
      <td style={{ padding: '10px 8px' }}>
        <div>{describePayment(p, isKo)}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {method ? (isKo ? method.ko : method.en) : (p.payment_method || '—')}
        </div>
      </td>
      <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div className="font-medium">{formatKrw(p.amount_krw)}</div>
        {/* A partial refund must never render as a bare word. Two numbers from
            the same row read unambiguously where one word does not. Suppressed
            when nothing has settled yet, so we never show a ₩0 refund. */}
        {back > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {isKo ? `${formatKrw(back)} 환불됨` : `${formatKrw(back)} refunded`}
          </div>
        )}
      </td>
      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
        <Chip view={PAYMENT_STATUS[p.status]} isKo={isKo} fallback={p.status} />
        {requested && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            {isKo ? '환불 요청 진행 중' : 'Refund request in progress'}
          </div>
        )}
      </td>
    </tr>
  );
}

function PaymentTable({ rows, isKo, openPaymentIds }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'left' }}>
            <th style={{ padding: '0 8px 8px' }}>{isKo ? '날짜' : 'Date'}</th>
            <th style={{ padding: '0 8px 8px' }}>{isKo ? '내역' : 'Description'}</th>
            <th style={{ padding: '0 8px 8px', textAlign: 'right' }}>{isKo ? '금액' : 'Amount'}</th>
            <th style={{ padding: '0 8px 8px', textAlign: 'right' }}>{isKo ? '상태' : 'Status'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <PaymentRow key={p.id} p={p} isKo={isKo} requested={openPaymentIds.has(p.id)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PaymentsTab() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  const [payments, setPayments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Booleans, not Error objects, so the effect stays [] and does not refetch on
  // a language change. The house pattern here is `catch { /* ignore */ }`, but
  // an invisible failure ON THIS TAB reproduces the exact complaint it exists to
  // answer: the customer cannot tell "you have no payments" from "we broke".
  const [paymentsFailed, setPaymentsFailed] = useState(false);
  const [requestsFailed, setRequestsFailed] = useState(false);

  useEffect(() => {
    (async () => {
      const [m, r] = await Promise.all([
        apiFetch('/api/v1/profile/membership').then((x) => x.json()).catch(() => null),
        apiFetch('/api/v1/refund-requests').then((x) => x.json()).catch(() => null),
      ]);
      if (m && Array.isArray(m.payments)) setPayments(m.payments);
      else setPaymentsFailed(true);
      if (Array.isArray(r)) setRequests(r);
      else if (r && Array.isArray(r.items)) setRequests(r.items);
      else setRequestsFailed(true);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div style={{ color: 'var(--text-secondary)' }}>{isKo ? '불러오는 중…' : 'Loading…'}</div>;
  }

  const settled = payments.filter((p) => !isUnsettled(p.status));
  const unsettled = payments.filter((p) => isUnsettled(p.status));
  const openPaymentIds = new Set(
    requests.filter((r) => !['closed', 'denied'].includes(r.status)).map((r) => r.payment_id),
  );
  const needsSupport = requests.some((r) => requestLabel(r, isKo)?.support);

  return (
    <div className="grid gap-6">
      <VPCard title={isKo ? '결제 내역' : 'Payment history'}>
        {paymentsFailed ? (
          <div style={{ color: '#991B1B' }}>
            {isKo ? '결제 내역을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
                  : 'Could not load your payment history. Please try again shortly.'}
          </div>
        ) : settled.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)' }}>
            {isKo ? '아직 결제 내역이 없습니다.' : 'No payments yet.'}
          </div>
        ) : (
          <PaymentTable rows={settled} isKo={isKo} openPaymentIds={openPaymentIds} />
        )}
      </VPCard>

      {/* Rendered only when non-empty. A pending wire row is the single most
          useful row on this tab — the customer who just filed a deposit request
          is exactly the person opening this screen — but it is not settled
          money and must not sit in the same table wearing the same styling. */}
      {unsettled.length > 0 && (
        <VPCard title={isKo ? '결제 대기 및 미완료' : 'Awaiting & incomplete'}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            {isKo ? '아직 결제가 확인되지 않은 요청입니다. 청구되지 않았습니다.'
                  : 'Requests where payment has not been confirmed. You have not been charged for these.'}
          </p>
          <PaymentTable rows={unsettled} isKo={isKo} openPaymentIds={openPaymentIds} />
        </VPCard>
      )}

      <VPCard title={isKo ? '환불 요청' : 'Refund requests'}>
        {requestsFailed ? (
          <div style={{ color: '#991B1B' }}>
            {isKo ? '환불 요청 내역을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
                  : 'Could not load your refund requests. Please try again shortly.'}
          </div>
        ) : requests.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)' }}>
            {isKo ? '환불 요청 내역이 없습니다.' : 'No refund requests.'}
          </div>
        ) : (
          <div className="grid gap-3">
            {requests.map((r) => {
              const view = requestLabel(r, isKo);
              return (
                <div key={r.id} style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: 14 }}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="font-medium">
                        {r.requested_months
                          ? (isKo ? `${r.requested_months}개월 환불 요청` : `Refund requested — ${r.requested_months} months`)
                          : (isKo ? '환불 요청' : 'Refund request')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {isKo ? '접수' : 'Submitted'} {ymd(r.submitted_at)}
                      </div>
                    </div>
                    <Chip view={view} isKo={isKo} fallback={r.status} />
                  </div>

                  <div className="mt-3" style={{ fontSize: 13 }}>
                    {/* The quoted figure is what the customer accepted. It is
                        shown for every state EXCEPT a declined one, where
                        repeating an amount they will not receive reads as a
                        promise. */}
                    {r.quoted_amount_krw != null && r.status !== 'denied' && (
                      <div className="flex justify-between py-0.5">
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {r.money_released ? (isKo ? '환불 금액' : 'Amount refunded')
                                            : (isKo ? '예상 환불 금액' : 'Quoted amount')}
                        </span>
                        <span className="font-medium">{formatKrw(r.quoted_amount_krw)}</span>
                      </div>
                    )}
                    {r.quoted_new_end_date && !r.money_released && r.status !== 'denied' && (
                      <div className="flex justify-between py-0.5">
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {isKo ? '환불 후 이용 종료일' : 'Term would end'}
                        </span>
                        <span>{ymd(r.quoted_new_end_date)}</span>
                      </div>
                    )}
                    {r.money_released && r.released_at && (
                      <div className="flex justify-between py-0.5">
                        <span style={{ color: 'var(--text-secondary)' }}>{isKo ? '지급일' : 'Released'}</span>
                        <span>{ymd(r.released_at)}</span>
                      </div>
                    )}
                  </div>

                  {view?.support && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 10 }}>
                      {isKo ? '처리가 예상보다 지연되고 있습니다. 지원팀에 문의해 주세요.'
                            : 'This is taking longer than expected. Please contact support.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 14 }}>
          {isKo ? `승인된 환불은 영업일 기준 ${REFUND_PROCESSING_DAYS}일 이내에 최초 결제 수단으로 처리됩니다.`
                : `Approved refunds are returned to the original payment method within ${REFUND_PROCESSING_DAYS} business days.`}
          {needsSupport && (isKo ? ' 문의: support@veripass.kr' : ' Contact: support@veripass.kr')}
        </p>
      </VPCard>
    </div>
  );
}
